import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Eye, Heart, Users, ArrowRight } from 'lucide-react';

export function AboutPage() {
  const values = [
    { icon: Target, title: 'Mission-Driven', description: 'Make HR simple and accessible for every organization' },
    { icon: Eye, title: 'Transparent', description: 'Open communication and honest pricing with our customers' },
    { icon: Heart, title: 'People-First', description: 'We build software that puts people at the center' },
    { icon: Users, title: 'Collaborative', description: 'We succeed when our customers succeed' },
  ];

  return (
    <>
      <SEO
        title="About Us - Nestor"
        description="Learn about our mission to simplify HR for enterprises worldwide. Meet our team and discover our values."
      />

      <section className="bg-gradient-to-b from-primary/10 via-background to-background py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Us</h1>
            <p className="text-xl text-muted-foreground">
              We're on a mission to make HR simple, fast, and delightful for organizations worldwide.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  We started Nestor after experiencing firsthand the frustration of using outdated, clunky HR systems. Traditional HRMS solutions were built decades ago and haven't kept up with how modern organizations work.
                </p>
                <p>
                  We believed there was a better way—one that puts user experience first, embraces modern technology, and truly understands the needs of today's workforce.
                </p>
                <p>
                  Today, we serve 500+ companies and 100,000+ users across 50+ countries, helping them manage their most valuable asset: their people.
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-6 text-center">Our Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <value.icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle>{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-6">Global Presence</h2>
              <p className="text-muted-foreground mb-6">
                We operate globally with teams across multiple continents, ensuring 24/7 support and local expertise for our customers.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Countries</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">500+</div>
                  <div className="text-sm text-muted-foreground">Companies</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">100K+</div>
                  <div className="text-sm text-muted-foreground">Users</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">99.9%</div>
                  <div className="text-sm text-muted-foreground">Uptime</div>
                </div>
              </div>
            </div>

            <div id="careers" className="mb-16">
              <h2 className="text-3xl font-bold mb-6">Join Our Team</h2>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground mb-4">
                    We're always looking for talented individuals who are passionate about building great products and helping organizations succeed.
                  </p>
                  <Button asChild>
                    <Link to="/contact">View Open Positions <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
